

// ── Tool registry ────────────────────────────────────────────────────────────
// Single source of truth for how each tool is classified in the agentic loop, so
// adding a tool means adding one row here instead of editing several scattered
// sets/switches (F1). Fields:
//   terminal       — stop the loop after it; results stay in the UI, no summary turn.
//   interactiveUI  — drives its own panel's UI when interactive mode is on.
//   confirm        — Confirm/Cancel gate policy:
//                      'auto'    → gate only when Auto-run is OFF (side-effecting)
//                      'replace' → gate when Auto-run OFF and it overwrites (write_document, non-append)
//                      'always'  → gate even when Auto-run is ON (destructive)
//                      'none'    → decided: no gate. Says "someone thought about
//                                  this" where absent only says "nobody wrote a row"
//                      (absent)  → never gated
// Only tools with non-default classification need a row.
export const TOOL_REGISTRY = {
  send_request:    { terminal: true, interactiveUI: true, confirm: 'auto' },
  run_collection:  { terminal: true, interactiveUI: true, confirm: 'auto' },
  // Returns up to 20 000 characters of a LIVE response body — whatever the
  // user's API actually returned, which on a production endpoint is real
  // customer data. It exists so the model can write meaningful tests against
  // a real payload, so the answer is a gate rather than removal: 'auto' asks
  // once per call unless the user has turned Auto-run on.
  get_last_response: { confirm: 'auto' },
  generate_tests:  {},
  // Same classification as generate_tests, for the same reason: it writes only
  // UNSAVED tab state that the user must review and save, so it is not the
  // saved-state mutation `update_request` gates with confirm:'always'. Not
  // terminal either — the dry run reports compile/runtime errors and the model
  // should read them and fix the script rather than stopping.
  generate_pre_request_script: {},
  update_request:  { confirm: 'always' },
  // 'none' rather than absent: the gate-coverage test asks for a DECISION on
  // anything whose name implies it changes state, and this one does. It stays
  // ungated because extract-a-token-then-set-a-variable is the backbone of
  // every chain, and a card there would interrupt work the user just asked for.
  set_variable:    { terminal: true, confirm: 'none' },
  // Creating adds; it does not overwrite or destroy. Gating creation would
  // make "build me these five endpoints" five interruptions for no safety.
  create_request:  { terminal: true, confirm: 'none' },
  db_run_sql:      { terminal: true, interactiveUI: true, confirm: 'auto' },
  // Chainable on purpose: the AI reads the command's output and continues.
  // 'auto' → every command shows the Confirm/Cancel card unless Auto-run is on.
  terminal_run_command: { confirm: 'auto' },
  write_document:  { terminal: true, interactiveUI: true, confirm: 'replace' },
  // Both OVERWRITE content the user wrote, the same reason write_document is
  // gated — and both shipped ungated because the registry is sparse and
  // nothing checked it (see aiToolGateCoverage.test.js). 'auto' keeps agentic
  // runs smooth: it yields to Auto-run, so only the mode that exists to
  // confirm each action actually confirms these.
  update_task:            { confirm: 'auto' },
  // Changes saved request state, exactly like update_request — and can point
  // a request at a different credential, so it is gated in every mode.
  set_auth:               { confirm: 'always' },
  // Changes which environment every subsequent request resolves against.
  switch_environment:     { confirm: 'auto' },
  // Creates collections and requests in bulk from a remote document — the
  // user should see what is about to land before it does.
  import_spec:            { confirm: 'auto' },
  write_task_description: { confirm: 'auto' },
  // Writes saved request state and changes what the local mock server serves.
  // Not 'always': it adds an example rather than destroying one, and an
  // agentic "mock these five endpoints" run should not stop five times.
  create_mock_example: { confirm: 'auto' },
  // Adds a saved connection; opens nothing and connects to nothing.
  create_stream_connection: { confirm: 'none' },
  create_environment: { confirm: 'none' },
  create_collection: { confirm: 'none' },
  // Batch (22.08.2026): the singular's gate — one card per CALL, never per item.
  create_folders:    { confirm: 'none' },
  // Read-only, rate-limited server-side; the card shows the query. Three
  // confirm cards in a row (search → fetch → import) would defeat the flow.
  web_search:        { confirm: 'none' },
  // An outbound request from the user's machine to a model-chosen URL —
  // send_request's class, so it yields to Auto-run and confirms otherwise.
  fetch_url:         { confirm: 'auto' },
  create_list:       { confirm: 'none' },
  create_task:       { confirm: 'none' },
  create_doc_folder: { confirm: 'none' },
  create_board:    { interactiveUI: true, confirm: 'none' },
  create_document: { interactiveUI: true, confirm: 'none' },
  delete_document: { confirm: 'always' },
  delete_board:    { confirm: 'always' },
};

export const registryNames = (pred) => Object.keys(TOOL_REGISTRY).filter((n) => pred(TOOL_REGISTRY[n]));

// Stop the tool loop after these — results stay in the UI (no extra summary turn).
// create_collection / create_folders / move_requests are chainable: feed results back so Claude can continue.
export const TERMINAL_TOOLS = new Set(registryNames((m) => m.terminal));

export const INTERACTIVE_UI_TOOLS = new Set(registryNames((m) => m.interactiveUI));

/** Whether a tool call must show the Confirm/Cancel gate before executing. */
export function toolNeedsConfirm(name, input, autoRun) {
  switch (TOOL_REGISTRY[name]?.confirm) {
    case 'always':  return true;                                   // destructive — even in Auto-run
    case 'auto':    return !autoRun;                               // side-effecting
    case 'replace': return !autoRun && input?.mode !== 'append';   // overwrites content
    default:        return false;
  }
}

export function resolveChainedToolInput(toolName, input, chainContext) {
  const resolved = { ...(input || {}) };
  if (toolName === 'create_folders' && !resolved.collection_id && chainContext.collectionId) {
    resolved.collection_id = chainContext.collectionId;
  }
  if (toolName === 'create_request') {
    if (!resolved.collection_id && chainContext.collectionId) {
      resolved.collection_id = chainContext.collectionId;
    }
    if (!resolved.folder_id && chainContext.folderId) {
      resolved.folder_id = chainContext.folderId;
    }
  }
  if (toolName === 'move_requests' && !resolved.collection_id && chainContext.collectionId) {
    resolved.collection_id = chainContext.collectionId;
  }
  if (toolName === 'run_collection' && !resolved.collection_id && chainContext.collectionId) {
    resolved.collection_id = chainContext.collectionId;
  }
  return resolved;
}

export function updateChainContext(toolName, result, chainContext) {
  if (result?.collection?.id) {
    chainContext.collectionId = String(result.collection.id);
  }
  // A batch auto-fills folder_id only when it produced exactly ONE folder —
  // after "make Deals, Contacts, Tasks" there is no right folder to default to.
  if (toolName === 'create_folders' && Array.isArray(result?.folders)) {
    if (result.collection_id) chainContext.collectionId = String(result.collection_id);
    const landed = result.folders.filter((f) => f && !f.error && f.folder_id);
    if (landed.length === 1) chainContext.folderId = String(landed[0].folder_id);
  }
  if (result?.request?.collection_id) {
    chainContext.collectionId = String(result.request.collection_id);
  }
  if (result?.request?.folder_id) {
    chainContext.folderId = String(result.request.folder_id);
  }
}
